import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Dimensions,
  SafeAreaView,
  Image,
  Platform,
  ActivityIndicator,
} from 'react-native';
// react-native-maps does not work on web - conditionally import
let MapView, Marker, PROVIDER_GOOGLE;
if (Platform.OS !== 'web') {
  const RNMaps = require('react-native-maps');
  MapView = RNMaps.default;
  Marker = RNMaps.Marker;
  PROVIDER_GOOGLE = RNMaps.PROVIDER_GOOGLE;
}
// Google Maps JS API for web
let GoogleMap, LoadScript, MarkerF;
if (Platform.OS === 'web') {
  const GMaps = require('@react-google-maps/api');
  GoogleMap = GMaps.GoogleMap;
  LoadScript = GMaps.LoadScript;
  MarkerF = GMaps.MarkerF;
}
import * as Location from 'expo-location';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import { rideService } from '../../services/rideService';
import { API_BASE_URL } from '../../config/api';

const logoImage = require('../../../assets/obzidn-logo.png');
const isWeb = Platform.OS === 'web';
const logoSrc = isWeb
  ? (typeof logoImage === 'string' ? logoImage : (logoImage && logoImage.default ? logoImage.default : '/obzidn-logo.png'))
  : null;

const { width } = Dimensions.get('window');

const showAlert = (title, message) => {
  if (isWeb) {
    window.alert(`${title}\n${message}`);
  } else {
    const { Alert } = require('react-native');
    Alert.alert(title, message);
  }
};

const GOOGLE_MAPS_API_KEY = 'AIzaSyD7c3Zwn9xAZ8byihOVxmMVIeczabqHLFU';

const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const CAR_MARKER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <circle cx="20" cy="20" r="18" fill="#000000" stroke="#C9A84C" stroke-width="1.5"/>
  <path d="M28 22l-1.5-4.5A2 2 0 0 0 24.6 16h-9.2a2 2 0 0 0-1.9 1.5L12 22v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h10v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-6zm-13.5-1l1-3h9l1 3h-11zm-0.5 4a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm12 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" fill="#ffffff"/>
</svg>`;

const MapScreen = ({ navigation }) => {
  const [location, setLocation] = useState(null);
  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [showInputs, setShowInputs] = useState(false);
  const [activeRide, setActiveRide] = useState(null);
  const [checkingRide, setCheckingRide] = useState(true);
  const [region, setRegion] = useState({
    latitude: 37.78825,
    longitude: -122.4324,
    latitudeDelta: 0.0922,
    longitudeDelta: 0.0421,
  });

  const [nearbyDrivers, setNearbyDrivers] = useState([]);

  const mapRef = useRef(null);
  const pollRef = useRef(null);
  const nearbyDriversPollRef = useRef(null);
  const { logout } = useAuth();

  // Check for active ride on mount and periodically
  const checkActiveRide = useCallback(async () => {
    try {
      const response = await rideService.getActiveRide();
      if (response.success && response.data.ride) {
        setActiveRide(response.data.ride);
        return response.data.ride;
      } else {
        setActiveRide(null);
        return null;
      }
    } catch (error) {
      console.log('[MapScreen] No active ride or error:', error.message);
      setActiveRide(null);
      return null;
    } finally {
      setCheckingRide(false);
    }
  }, []);

  useEffect(() => {
    getCurrentLocation();
    // Check for active ride immediately
    checkActiveRide();
    // Poll every 8 seconds
    pollRef.current = setInterval(checkActiveRide, 8000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [checkActiveRide]);

  // Listen for focus events to re-check active ride
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      checkActiveRide();
    });
    return unsubscribe;
  }, [navigation, checkActiveRide]);

  // Fetch nearby drivers for map markers (web only)
  const fetchNearbyDrivers = useCallback(async () => {
    if (!isWeb || !location) return;
    try {
      const response = await rideService.getNearbyDrivers(location.latitude, location.longitude);
      if (response.success && Array.isArray(response.data?.drivers)) {
        setNearbyDrivers(response.data.drivers);
      }
    } catch (error) {
      console.log('[MapScreen] fetchNearbyDrivers error:', error.message);
    }
  }, [location]);

  // Poll nearby drivers every 10 seconds (web only)
  useEffect(() => {
    if (!isWeb || !location) return;
    fetchNearbyDrivers();
    nearbyDriversPollRef.current = setInterval(fetchNearbyDrivers, 10000);
    return () => {
      if (nearbyDriversPollRef.current) clearInterval(nearbyDriversPollRef.current);
    };
  }, [location, fetchNearbyDrivers]);

  const getCurrentLocation = async () => {
    if (isWeb) {
      // Web: use browser Geolocation API
      if (!navigator.geolocation) {
        showAlert('Not supported', 'Geolocation is not supported by your browser. Please type your pickup location.');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ latitude, longitude });
          setRegion({ latitude, longitude, latitudeDelta: 0.01, longitudeDelta: 0.01 });
          setPickup(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        },
        (err) => {
          console.log('[MapScreen] Web geolocation error:', err.message);
          showAlert('Location unavailable', 'Could not get your location. Please type your pickup address.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
      return;
    }

    // Native: use expo-location
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        showAlert('Permission denied', 'Location permission is required to use this app');
        return;
      }

      const currentLocation = await Location.getCurrentPositionAsync({});
      const { latitude, longitude } = currentLocation.coords;

      setLocation({ latitude, longitude });
      setRegion({
        latitude,
        longitude,
        latitudeDelta: 0.01,
        longitudeDelta: 0.01,
      });

      try {
        const address = await Location.reverseGeocodeAsync({ latitude, longitude });
        if (address.length > 0) {
          const currentAddress = `${address[0].street || ''} ${address[0].city || ''}`.trim();
          if (currentAddress) setPickup(currentAddress);
        }
      } catch (geoErr) {
        console.log('[MapScreen] Reverse geocode failed:', geoErr.message);
        setPickup(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      }
    } catch (error) {
      console.error('Error getting location:', error);
      showAlert('Error', 'Could not get your location');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleRequestRide = () => {
    if (!pickup || !destination) {
      showAlert('Error', 'Please enter both pickup and destination locations');
      return;
    }

    navigation.navigate('RequestRide', {
      pickup,
      destination,
      pickupCoords: location,
    });
  };

  const handleContinueRide = () => {
    if (activeRide) {
      if (activeRide.status === 'COMPLETED') {
        navigation.navigate('RateRide', { ride: activeRide });
      } else {
        navigation.navigate('TrackDriver', { ride: activeRide });
      }
    }
  };

  const toggleInputs = () => {
    setShowInputs(!showInputs);
  };

  const handleLimousineBooking = () => {
    navigation.navigate('BookLimousine', {
      pickup: pickup || 'Current Location',
      dropoff: 'Enter destination',
      pickupCoords: location,
      dropoffCoords: null,
      distance: 10
    });
  };

  const getActiveRideStatusText = () => {
    if (!activeRide) return '';
    switch (activeRide.status) {
      case 'REQUESTED': return 'Looking for a driver...';
      case 'ACCEPTED': return 'Driver is on the way!';
      case 'ARRIVED': return 'Driver has arrived!';
      case 'IN_PROGRESS': return 'Ride in progress';
      default: return activeRide.status;
    }
  };

  const getActiveRideStatusColor = () => {
    if (!activeRide) return '#666';
    switch (activeRide.status) {
      case 'REQUESTED': return '#FF9800';
      case 'ACCEPTED': return '#2196F3';
      case 'ARRIVED': return '#9C27B0';
      case 'IN_PROGRESS': return '#4CAF50';
      default: return '#666';
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {isWeb ? (
            <img src={logoSrc} alt="OBZIDN" style={{width: 40, height: 40, objectFit: 'contain', marginRight: 10}} />
          ) : (
            <Image source={logoImage} style={styles.headerLogo} resizeMode="contain" />
          )}
          <Text style={styles.headerTitle}>OBZIDN</Text>
        </View>
        <TouchableOpacity onPress={handleLogout} style={styles.logoutButton}>
          <Ionicons name="log-out-outline" size={24} color="#999" />
        </TouchableOpacity>
      </View>

      {/* Active Ride Banner */}
      {activeRide && (
        <TouchableOpacity style={[styles.activeRideBanner, { borderLeftColor: getActiveRideStatusColor() }]} onPress={handleContinueRide}>
          <View style={styles.activeRideBannerLeft}>
            <View style={[styles.activeRideDot, { backgroundColor: getActiveRideStatusColor() }]} />
            <View style={styles.activeRideBannerInfo}>
              <Text style={styles.activeRideStatusText}>{getActiveRideStatusText()}</Text>
              <Text style={styles.activeRideDestText} numberOfLines={1}>{activeRide.destination}</Text>
            </View>
          </View>
          <View style={styles.activeRideBannerRight}>
            <Text style={styles.activeRideTapText}>TAP TO VIEW</Text>
            <Ionicons name="chevron-forward" size={18} color="#000" />
          </View>
        </TouchableOpacity>
      )}

      {/* Map */}
      <View style={styles.mapContainer}>
        {isWeb ? (
          <>
          <LoadScript
            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
            loadingElement={
              <View style={styles.webMapPlaceholder}>
                <ActivityIndicator size="large" color="#C9A84C" />
                <Text style={styles.webMapText}>Loading Map...</Text>
              </View>
            }
          >
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={
                location
                  ? { lat: location.latitude, lng: location.longitude }
                  : { lat: 37.78825, lng: -122.4324 }
              }
              zoom={14}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                styles: DARK_MAP_STYLE,
              }}
            >
              {/* User location blue dot */}
              {location && (
                <MarkerF
                  position={{ lat: location.latitude, lng: location.longitude }}
                  icon={{
                    path: 'M 0, 0 m -8, 0 a 8,8 0 1,0 16,0 a 8,8 0 1,0 -16,0',
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                    scale: 1,
                  }}
                  title="Your Location"
                />
              )}

              {/* Nearby driver car markers */}
              {nearbyDrivers.map((driver) => (
                <MarkerF
                  key={driver.id}
                  position={{ lat: driver.latitude, lng: driver.longitude }}
                  icon={{
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(CAR_MARKER_SVG)}`,
                    scaledSize: { width: 36, height: 36 },
                  }}
                  title="Available Driver"
                />
              ))}
            </GoogleMap>
          </LoadScript>
          {/* Recenter button for web */}
          {location && (
            <TouchableOpacity
              style={styles.myLocationButton}
              onPress={getCurrentLocation}
            >
              <Ionicons name="locate" size={24} color="#000000" />
            </TouchableOpacity>
          )}
          </>
        ) : (
          <>
            <MapView
              ref={mapRef}
              style={styles.map}
              provider={PROVIDER_GOOGLE}
              region={region}
              showsUserLocation={true}
              showsMyLocationButton={false}
              onRegionChangeComplete={setRegion}
            >
              {location && (
                <Marker
                  coordinate={location}
                  title="Your Location"
                  pinColor="blue"
                />
              )}
            </MapView>

            <TouchableOpacity
              style={styles.myLocationButton}
              onPress={getCurrentLocation}
            >
              <Ionicons name="locate" size={24} color="#000000" />
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* Bottom Panel */}
      <View style={styles.bottomPanel}>
        {/* Service Type Quick Access */}
        <View style={styles.serviceTypeQuick}>
          <TouchableOpacity
            style={styles.serviceTypeCard}
            onPress={toggleInputs}
          >
            <Ionicons name="car" size={28} color="#000" />
            <Text style={styles.serviceTypeTitle}>Standard</Text>
            <Text style={styles.serviceTypeSubtitle}>Regular rides</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.serviceTypeCard, styles.serviceTypeCardPremium]}
            onPress={handleLimousineBooking}
          >
            <Ionicons name="car-sport" size={28} color="#D4AF37" />
            <Text style={[styles.serviceTypeTitle, styles.serviceTypeTitlePremium]}>Limousine</Text>
            <Text style={styles.serviceTypeSubtitle}>Premium luxury</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.serviceTypeCard}
            onPress={() => navigation.navigate('CorporateRegistration')}
          >
            <Ionicons name="briefcase" size={28} color="#000" />
            <Text style={styles.serviceTypeTitle}>Corporate</Text>
            <Text style={styles.serviceTypeSubtitle}>Business</Text>
          </TouchableOpacity>
        </View>

        {!showInputs ? (
          <TouchableOpacity
            style={styles.whereToButton}
            onPress={toggleInputs}
          >
            <Text style={styles.whereToText}>Where to?</Text>
            <Ionicons name="chevron-up" size={24} color="#666666" />
          </TouchableOpacity>
        ) : (
          <View style={styles.inputContainer}>
            <View style={styles.inputRow}>
              <View style={styles.inputIcon}>
                <View style={styles.pickupDot} />
              </View>
              <TextInput
                style={styles.input}
                placeholder="Pickup location"
                placeholderTextColor="#666666"
                value={pickup}
                onChangeText={setPickup}
              />
            </View>

            <View style={styles.inputRow}>
              <View style={styles.inputIcon}>
                <View style={styles.destinationDot} />
              </View>
              <TextInput
                style={styles.input}
                placeholder="Where to?"
                placeholderTextColor="#666666"
                value={destination}
                onChangeText={setDestination}
              />
            </View>

            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={styles.cancelInputButton}
                onPress={toggleInputs}
              >
                <Text style={styles.cancelInputButtonText}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.requestButton,
                  (!pickup || !destination) && styles.requestButtonDisabled
                ]}
                onPress={handleRequestRide}
                disabled={!pickup || !destination}
              >
                <Text style={styles.requestButtonText}>Request Ride</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    minHeight: 60,
    paddingVertical: 10,
    backgroundColor: '#0a0a0a',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 40,
    height: 40,
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#C9A84C',
    letterSpacing: 4,
  },
  logoutButton: {
    padding: 8,
  },
  // Active ride banner
  activeRideBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderLeftWidth: 4,
  },
  activeRideBannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  activeRideDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 12,
  },
  activeRideBannerInfo: {
    flex: 1,
  },
  activeRideStatusText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#333',
  },
  activeRideDestText: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  activeRideBannerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  activeRideTapText: {
    fontSize: 11,
    fontWeight: '800',
    color: '#000',
    letterSpacing: 1,
    marginRight: 4,
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
  },
  map: {
    width: width,
    height: '100%',
  },
  webMapPlaceholder: {
    flex: 1,
    backgroundColor: '#1a1a2e',
    justifyContent: 'center',
    alignItems: 'center',
  },
  webMapText: {
    color: '#C9A84C',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 12,
  },
  webMapSubtext: {
    color: '#999',
    fontSize: 13,
    marginTop: 6,
  },
  myLocationButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: '#ffffff',
    borderRadius: 25,
    width: 50,
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  bottomPanel: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 20,
    paddingVertical: 15,
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  serviceTypeQuick: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  serviceTypeCard: {
    flex: 1,
    backgroundColor: '#F8F8F8',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#E0E0E0',
  },
  serviceTypeCardPremium: {
    backgroundColor: '#FFF8E7',
    borderColor: '#D4AF37',
  },
  serviceTypeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
    marginTop: 6,
  },
  serviceTypeTitlePremium: {
    color: '#D4AF37',
  },
  serviceTypeSubtitle: {
    fontSize: 10,
    color: '#666',
    marginTop: 2,
  },
  whereToButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
  },
  whereToText: {
    fontSize: 18,
    color: '#333333',
    fontWeight: '500',
  },
  inputContainer: {
    paddingVertical: 10,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  inputIcon: {
    width: 20,
    alignItems: 'center',
    marginRight: 15,
  },
  pickupDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4CAF50',
  },
  destinationDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#F44336',
  },
  input: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    paddingVertical: 10,
    fontSize: 16,
    color: '#333333',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
  },
  cancelInputButton: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    borderRadius: 8,
    paddingVertical: 15,
    alignItems: 'center',
    marginRight: 10,
  },
  cancelInputButtonText: {
    color: '#666666',
    fontSize: 16,
    fontWeight: '500',
  },
  requestButton: {
    flex: 2,
    backgroundColor: '#000000',
    borderRadius: 8,
    paddingVertical: 15,
    alignItems: 'center',
    marginLeft: 10,
  },
  requestButtonDisabled: {
    backgroundColor: '#CCCCCC',
  },
  requestButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default MapScreen;
